import React from 'react';
import dynamic from 'next/dynamic';
import {MUIBox, MUITypography} from '@/components/MUI-Components';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import LinkIcon from '@mui/icons-material/Link';
import Tooltip from '@mui/material/Tooltip';
import {colors} from '@/styles/tokens';
import {formatViews} from '@/utils/formatters';
import {shareLinks} from '@/utils/social';
import {ShareXIcon, ShareLinkedInIcon, ShareFacebookIcon, ShareInstagramIcon, GoogleSourceCta} from '@/components/shared/icons/SocialIcons';
import AudioPlayerView from '@/components/shared/audio/AudioPlayerView';

// Loaded client-side only so the Web Speech engine ships as a separate chunk and
// never bundles into pages that don't render the audio player. Until it loads, the same
// markup without the speech logic holds its space, so the views and share row don't shift.
const ArticleAudioPlayer = dynamic(() => import('@/components/shared/audio/AudioPlayer'), {
    ssr: false,
    loading: () => <AudioPlayerView />
});

// Audio player + view count + share row. Presentational — JSX copied verbatim.
const BlogMediaBar = ({
    title,
    html,
    viewCount,
    shareUrl,
    instagramMsg,
    handleInstagramShare,
    isCopied,
    handleCopyLink,
    isBookmarked,
    handleBookmark
}: {
    title: string;
    html: string;
    viewCount: number | null;
    shareUrl: string;
    instagramMsg: string;
    handleInstagramShare: () => void;
    isCopied: boolean;
    handleCopyLink: () => void;
    isBookmarked: boolean;
    handleBookmark: () => void;
}) => (
    <MUIBox className="bdn-media-bar">
        {/* Audio player */}
        <ArticleAudioPlayer title={title} html={html} />

        {/* Views */}
        <MUIBox className="bdn-views">
            <VisibilityOutlinedIcon className="bdn-views-icon" />
            <MUITypography className="bdn-views-text">
                <strong>{viewCount !== null && viewCount !== undefined ? formatViews(viewCount) : '—'}</strong> views
            </MUITypography>
        </MUIBox>

        {/* Share */}
        <MUIBox className="bdn-share-row">
            <MUITypography className="bdn-share-label">Share</MUITypography>
            <MUIBox className="bdn-share-icons">
                <Tooltip title="X (Twitter)" placement="top" arrow>
                    <a href={shareLinks.twitter(shareUrl)} target="_blank" rel="noopener noreferrer" aria-label="Share on X">
                        <ShareXIcon />
                    </a>
                </Tooltip>
                <Tooltip title="LinkedIn" placement="top" arrow>
                    <a href={shareLinks.linkedin(shareUrl)} target="_blank" rel="noopener noreferrer" aria-label="Share on LinkedIn">
                        <ShareLinkedInIcon />
                    </a>
                </Tooltip>
                <Tooltip title="Facebook" placement="top" arrow>
                    <a href={shareLinks.facebook(shareUrl)} target="_blank" rel="noopener noreferrer" aria-label="Share on Facebook">
                        <ShareFacebookIcon />
                    </a>
                </Tooltip>
                <Tooltip title={instagramMsg || 'Share on Instagram'} placement="top" arrow open={!!instagramMsg || undefined}>
                    <button aria-label="Share on Instagram" onClick={handleInstagramShare}>
                        <ShareInstagramIcon />
                    </button>
                </Tooltip>
                <Tooltip title={isCopied ? 'Copied!' : 'Copy Link'} placement="top" arrow>
                    <button aria-label="Copy link" onClick={handleCopyLink}>
                        {/* Size is fixed at 18px by the .bdn-share-icons svg rule; only the
                            state-dependent color stays inline. */}
                        <LinkIcon sx={{color: isCopied ? colors.primary.main : 'currentColor'}} />
                    </button>
                </Tooltip>
                <Tooltip title={isBookmarked ? 'Bookmarked' : 'Bookmark'} placement="top" arrow>
                    <button aria-label="Bookmark" onClick={handleBookmark}>
                        {isBookmarked ? <BookmarkIcon className="bdn-bookmark-filled" /> : <BookmarkBorderIcon />}
                    </button>
                </Tooltip>
            </MUIBox>
        </MUIBox>

        {/* Mobile-only Google CTA — mirrors the one in bdn-author-bar */}
        <GoogleSourceCta className="bdn-google-cta--mobile" />
    </MUIBox>
);

export default BlogMediaBar;
